import { memo, useCallback, useEffect } from 'react'
import { AnimatePresence, motion, type Transition, type Variants } from 'framer-motion'
import { useIsChatAwaitingNextDrain, useQueuedMessages } from '../../../store'
import { useCancelQueuedMessage } from '../../../hooks/useCancelMessage'
import { useMessageListScrollFlags, useScrollTo } from '../message-list/scroll-atoms'
import { QueuedBubble } from './nested/queued-bubble'

type QueuedMessagesPanelProps = {
  chatId: string | undefined
}

const panelVariants: Variants = {
  initial: { height: 0, opacity: 0 },
  animate: { height: 'auto', opacity: 1 },
  exit: { height: 0, opacity: 0 },
}

const panelTransition: Transition = {
  duration: 0.18,
  ease: [0.4, 0, 0.2, 1],
}

export const QueuedMessagesPanel = memo(({ chatId }: QueuedMessagesPanelProps) => {
  const queued = useQueuedMessages(chatId)
  const cancelQueuedMessage = useCancelQueuedMessage(chatId)
  const isAwaitingNextDrain = useIsChatAwaitingNextDrain(chatId)
  const { autoScroll } = useMessageListScrollFlags()
  const scrollTo = useScrollTo()

  // The panel sits below the message list and steals vertical space when it
  // mounts or grows, which pushes the last assistant message above the
  // message-list viewport. Re-anchor at the bottom whenever the queue grows
  // (or first appears) so the latest content stays in view. Only re-anchor
  // if the user hasn't intentionally scrolled away.
  const queuedLength = queued.length
  useEffect(() => {
    if (queuedLength === 0 || !autoScroll) return
    scrollTo({ direction: 'bottom', behavior: 'smooth' })
  }, [queuedLength, autoScroll, scrollTo])

  // commitTurnStart's scrollToEnd fires when 'metadata' arrives, but the
  // panel still takes its full height for another ~180ms while the exit
  // animation collapses it. After collapse the message-list viewport has
  // grown and the freshly-added assistant placeholder is no longer at the
  // bottom. Re-anchor once the exit animation actually completes.
  const handleExitComplete = useCallback(() => {
    if (!autoScroll) return
    scrollTo({ direction: 'bottom', behavior: 'smooth' })
  }, [autoScroll, scrollTo])

  return (
    <AnimatePresence initial={false} onExitComplete={handleExitComplete}>
      {chatId && queuedLength > 0 && (
        <motion.div
          key="queued-messages-panel"
          variants={panelVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={panelTransition}
          className="w-full overflow-hidden"
        >
          <div role="list" aria-label="Queued messages" className="flex w-full flex-col items-end gap-2 px-3 pb-2 pt-3">
            {queued.map((item, index) => (
              <QueuedBubble
                key={item.localId}
                item={item}
                onCancel={cancelQueuedMessage}
                isActive={index === 0 && isAwaitingNextDrain}
              />
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
})

QueuedMessagesPanel.displayName = 'QueuedMessagesPanel'
